import React, { useState } from 'react';
import { MSL_TABLE_HEADER_SUBTEXT } from '@constants/commonConstants';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import InnerTableHeader from '@molecules/msl/components/forecast/innerTableHeader';
import ReactDOM from 'react-dom';
import { getOpportunitiesDue } from '@services/commonServices';
import { Current_User, Active_Tab_Msl, System_Date_Time_State } from '@recoil/recoil';
import { useRecoilValue } from 'recoil';

function formatCurrencyWithoutCents(amount) {
  // Convert to number (ensure it handles cases like "0.000", "1200.00")
  const numericValue = parseFloat(amount); // Default to 0 if null or invalid
  // Handle the special case for 0.000 to show "$0"
  if (numericValue === 0) {
    return '$0';
  }
  // Remove the decimals by flooring the value to the nearest dollar
  const dollarsOnly = Math.floor(numericValue); // Truncate decimals
  // Format the number as currency with commas (US-style grouping)
  const formattedValue = dollarsOnly.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Prepend the dollar sign
  return `$${formattedValue}`;
}

// General cellRenderer function to handle multiple fields
function currencyCellRenderer(params, fieldName) {
  // If the row is a group row, display the formatted amount for the group
  if (params.node.group) {
    const allLeafChildren = params?.node?.allLeafChildren || [];
    const totalAmount = allLeafChildren.reduce(
      (acc, childNode) => acc + (childNode?.data?.[fieldName] || 0),
      0,
    );
    return formatCurrencyWithoutCents(totalAmount);
  }

  // For normal rows, format the specific field value
  return formatCurrencyWithoutCents(params.data[fieldName]);
}

const StatusCellRenderer = props => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    headerTitle: '',
    bodyContent: '',
    closeButtonText: 'Close',
  });
  const [month, setMonth] = useState(''); // State to store the month
  const currentUser = useRecoilValue(Current_User);
  const tabActive = useRecoilValue(Active_Tab_Msl);
  const systemDateTime = useRecoilValue(System_Date_Time_State);

  // Function to fetch the month based on tabActive
  const fetchMonth = async () => {
    try {
      const opportunitiesDueAPI = await getOpportunitiesDue(
        currentUser?.role,
        currentUser?.email,
        systemDateTime?.split('T')[0],
      );

      let MONTH;
      if (tabActive === 0) {
        MONTH = opportunitiesDueAPI?.month; // Current month
      } else if (tabActive === 1) {
        MONTH = opportunitiesDueAPI?.next_month; // Next month
      } else if (tabActive === 2) {
        MONTH = opportunitiesDueAPI?.next2next_month; // Next-to-next month
      }

      setMonth(MONTH); // Update the state with the fetched month
    } catch (error) {
      console.error('Error fetching month:', error);
    }
  };

  // Use useEffect to fetch the month
  React.useEffect(() => {
    fetchMonth();
  }, []);
  const handleViewCommentClick = () => {
    // For grouped rows (Account Totals), get comments from GCSP Judge child row
    let commentText = 'No comment available.';
    if (props.node.group && props?.node?.allLeafChildren) {
      const gcspJudgeRow = props.node.allLeafChildren.find(
        child => child?.data?.value_type === 'GCSP Judge',
      );
      commentText = gcspJudgeRow?.data?.comments || 'No comment available.';
    }

    setModalContent({
      headerTitle: `View Comment: ${month || 'Month'}`, // Use the fetched month in the header
      bodyContent: commentText,
      closeButtonText: 'Close',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const Modal = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '90%',
          padding: '0',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            backgroundColor: '#333',
            padding: '15px',
            color: 'white',
            fontSize: '18px',
            fontWeight: 'bold',
            textAlign: 'left',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          {modalContent.headerTitle}
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: '20px',
            fontSize: '14px',
            color: '#555',
            lineHeight: '1.5',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {modalContent.bodyContent}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '15px',
            textAlign: 'left',
            borderTop: '1px solid #ddd',
          }}
        >
          <button
            type="button"
            onClick={closeModal}
            style={{
              padding: '10px 20px',
              backgroundColor: '#fff',
              color: '#000',
              border: '1px solid #000',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            {modalContent.closeButtonText}
          </button>
        </div>
      </div>
    </div>
  );

  // For grouped rows (Account Totals), extract comments from GCSP Judge child row
  let groupComments = null;
  if (props.node.group && props?.node?.allLeafChildren) {
    const gcspJudgeRow = props.node.allLeafChildren.find(
      child => child?.data?.value_type === 'GCSP Judge',
    );
    groupComments = gcspJudgeRow?.data?.comments;
  }

  //existing code for decorating the status cell with border and color
  if (props.node.group && props.value) {
    const groupStatus = props?.node?.allLeafChildren[1]?.parent?.allLeafChildren[1]?.data?.status;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ minWidth: '90px' }}>
          <span
            style={{
              color: '#2E2E38', // Inner text color is always black
              padding: '3px 10px',
              borderRadius: '50px', // Oval border
              border: `1px solid ${props.value === 'Submitted' || props.value === 'N/A' ? '#168736' : '#B9251C'}`,
            }}
          >
            {groupStatus}
          </span>
        </div>
        {groupComments ? (
          <button //for true case where Comments exist
            type="button"
            onClick={handleViewCommentClick}
            style={{
              textDecoration: 'none',
              color: '#155cb4',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              marginLeft: '10px',
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
          >
            View Comment
          </button>
        ) : (
          <span //for false case where Comments do not exist
            style={{
              marginLeft: '10px',
              color: '#747480',
              fontWeight: 500,
              cursor: 'not-allowed',
              textDecoration: 'none',
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
          >
            View Comment
          </span>
        )}
        {isModalOpen && ReactDOM.createPortal(<Modal />, document.body)}
      </div>
    );
  } else {
    // Handle normal row text
    if (!props.value) {
      // If the value is blank, return null or an empty span with no design
      return null;
    }

    return (
      <div>
        <span
          style={{
            color: '#2E2E38', // Inner text color is always black
            padding: '3px 10px',
            borderRadius: '50px', // Oval border
            border: `1px solid ${props.value === 'Submitted' || props.value === 'N/A' ? '#168736' : '#B9251C'}`,
          }}
        >
          {props.value}
        </span>
      </div>
    );
  }
};

const cellTextAligment = { textAlign: 'right' };
export const columnDefsKeys2 = [
  {
    headerName: 'Accounts',
    field: 'name',
    filter: 'agTextColumnFilter', // Use the default text filter
    filterValueGetter: params => {
      // For grouped rows, return the group key or a placeholder
      if (params.node.group) {
        return params.node.key; // Use the group key for filtering grouped rows
      }

      // For non-grouped rows, return the account name
      return params.data?.name || ''; // Ensure it returns an empty string if name is undefined
    },
    suppressMovable: true,
    cellRenderer: 'agGroupCellRenderer', // Render grouped rows
    sortable: true,
    minWidth: 180,
    rowGroup: true, // Enable row grouping
    hide: true, // Hide the column from the grid (it will be displayed via autoGroupColumnDef)
  },
  {
    headerName: 'GCSP',
    field: 'gcsp',
    minWidth: 120,
    hide: window.innerWidth < 600,
    sortable: true,
    suppressMovable: true,
    tooltipField: 'gcsp',
    filter: 'agTextColumnFilter',
    filterValueGetter: params => {
      // For grouped rows, return the group key or a placeholder
      if (params?.node?.group) {
        return params?.node?.key; // Use the group key for filtering grouped rows
      }

      // For non-grouped rows, return the account name
      return params?.data?.gcsp || ''; // Ensure it returns an empty string if name is undefined
    },
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableFilter: true,
      enableSorting: false,
    },
    cellRenderer: params => {
      if (params.node.group) {
        // Ensure allLeafChildren exists and has data
        if (params.node.allLeafChildren && params.node.allLeafChildren.length > 0) {
          // Get the first item in the allLeafChildren array and check if category exists
          const firstLeaf = params.node.allLeafChildren[0].data.gcsp;
          return firstLeaf;
        }
        return ''; // Fallback if data is not available
      } else {
        // For normal rows, return an empty string to hide the value
        return '';
      }
    },
  },

  {
    headerName: 'Value Type',
    field: 'value_type',
    sortable: true,
    aggFunc: 'first',
    tooltipField: 'value_type',
    suppressMovable: true,
    valueFormatter: params => {
      // Show custom text for group rows
      return params.node.group ? 'Account Totals' : params.value;
    },
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: false,
      enableMenu: false,
      enableFilter: false,
    },
    minWidth: 120,
    hide: window.innerWidth < 600,
  },

  {
    headerName: 'Status',
    field: 'status',
    aggFunc: 'last',
    tooltipField: 'status',
    minWidth: 220, //to accomodate the View Comment with Status
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: false,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: StatusCellRenderer,
  },

  {
    headerName: 'Assurance',
    field: 'assurance',
    sortable: true,
    aggFunc: 'sum',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    tooltipValueGetter: params => {
      let assuranceValue;
      if (params.node.group) {
        assuranceValue = params?.value || 0;
      } else {
        assuranceValue = params?.data?.assurance || 0;
      }
      const formattedAssuranceValue = formatCurrencyWithoutCents(assuranceValue);
      return `${formattedAssuranceValue}`;
    },
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_ACR,
    },

    cellRenderer: params => currencyCellRenderer(params, 'assurance'),
  },
  {
    headerName: 'Consulting',
    field: 'consulting',
    tooltipValueGetter: params => {
      let consultingValue;
      if (params.node.group) {
        consultingValue = params?.value || 0;
      } else {
        consultingValue = params?.data?.consulting || 0;
      }
      const formattedConsultingValue = formatCurrencyWithoutCents(consultingValue);
      return `${formattedConsultingValue}`;
    },
    minWidth: 150,
    hide: window.innerWidth < 600,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => currencyCellRenderer(params, 'consulting'),
  },
  {
    headerName: 'SAT',
    field: 'sat',
    tooltipValueGetter: params => {
      let satValue;
      if (params.node.group) {
        satValue = params?.value || 0;
      } else {
        satValue = params?.data?.sat || 0;
      }
      const formattedSatValue = formatCurrencyWithoutCents(satValue);
      return `${formattedSatValue}`;
    },
    minWidth: 150,
    hide: window.innerWidth < 600,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    headerComponent: InnerTableHeader,
    cellRenderer: params => currencyCellRenderer(params, 'sat'),
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_SAT,
    },
  },
  {
    headerName: 'Tax',
    field: 'tax',
    tooltipValueGetter: params => {
      let taxValue;
      if (params.node.group) {
        taxValue = params?.value || 0;
      } else {
        taxValue = params?.data?.tax || 0;
      }
      const formattedTaxValue = formatCurrencyWithoutCents(taxValue);
      return `${formattedTaxValue}`;
    },
    sortable: true,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    minWidth: 150,
    hide: window.innerWidth < 600,
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_TAX,
    },
    cellRenderer: params => currencyCellRenderer(params, 'tax'),
  },
  {
    headerName: 'ITTS',
    field: 'itts',
    tooltipValueGetter: params => {
      let ittsValue;
      if (params.node.group) {
        ittsValue = params?.value || 0;
      } else {
        ittsValue = params?.data?.itts || 0;
      }
      const formattedIttsValue = formatCurrencyWithoutCents(ittsValue);
      return `${formattedIttsValue}`;
    },
    minWidth: 150,
    hide: window.innerWidth < 600,
    aggFunc: 'sum',
    suppressMovable: true,
    cellStyle: cellTextAligment,
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => currencyCellRenderer(params, 'itts'),
  },
  {
    headerName: 'EMEIA ACR',
    field: 'emiaacr',
    tooltipValueGetter: params => {
      let emiaacrValue;
      if (params.node.group) {
        emiaacrValue = params?.value || 0;
      } else {
        emiaacrValue = params?.data?.emiaacr || 0;
      }
      const formattedEmiaacrValue = formatCurrencyWithoutCents(emiaacrValue);
      return `${formattedEmiaacrValue}`;
    },
    aggFunc: 'sum',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => currencyCellRenderer(params, 'emiaacr'),
  },
  {
    headerName: 'Total',
    field: 'total',
    tooltipValueGetter: params => {
      let totalValue;
      if (params.node.group) {
        totalValue = params?.value || 0;
      } else {
        totalValue = params?.data?.total || 0;
      }
      const formattedTotalValue = formatCurrencyWithoutCents(totalValue);
      return `${formattedTotalValue}`;
    },
    aggFunc: 'sum',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    headerComponent: InnerTableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => currencyCellRenderer(params, 'total'),
  },
];
